import { useEffect, useRef, useState } from "react";
import { apiFetch } from "../../../utils/apiFetch";
import { ansprechpartnerAus, type ApPerson } from "./ansprechpartner";

/**
 * Ansprechpartner einer Firma laden (KomCell-Muster: ein Abruf des
 * Kontakt-Details mit `include=relations,relations-deep`).
 *
 * `aktiv=false` hält den Abruf zurück — die chronologische Ansicht holt beim
 * Öffnen eines Vorgangs schon vier Listen und lädt die Ansprechpartner deshalb
 * erst beim Aufklappen. Einmal geladen bleibt das Ergebnis stehen; Zu- und
 * Aufklappen fragt nicht erneut.
 */
export function useAnsprechpartner(
    kontaktId: number | null,
    aktiv = true,
): { personen: ApPerson[]; istFirma: boolean; geladen: boolean } {
    const [personen, setPersonen] = useState<ApPerson[]>([]);
    const [istFirma, setIstFirma] = useState(false);
    const [geladen, setGeladen] = useState(false);
    const geladenFuer = useRef<number | null>(null);

    useEffect(() => {
        if (kontaktId == null) {
            geladenFuer.current = null;
            setPersonen([]);
            setIstFirma(false);
            setGeladen(false);
            return;
        }
        // Kontaktwechsel verwirft das alte Ergebnis, auch wenn gerade nicht
        // geladen werden darf — sonst zeigt die Zeile die Ansprechpartner des
        // vorigen Kunden.
        if (geladenFuer.current !== kontaktId) {
            geladenFuer.current = null;
            setPersonen([]);
            setIstFirma(false);
            setGeladen(false);
        }
        if (!aktiv || geladenFuer.current === kontaktId) return;

        let abgebrochen = false;
        apiFetch<any>(`contact/${kontaktId}`, {
            params: { include: "relations,relations-deep" },
        })
            .then(({ data }) => {
                if (abgebrochen) return;
                const ergebnis = ansprechpartnerAus(data);
                geladenFuer.current = kontaktId;
                setPersonen(ergebnis.personen);
                setIstFirma(ergebnis.istFirma);
            })
            .catch((err) => console.error("[useAnsprechpartner] Laden fehlgeschlagen", err))
            .finally(() => {
                if (!abgebrochen) setGeladen(true);
            });

        return () => {
            abgebrochen = true;
        };
    }, [kontaktId, aktiv]);

    return { personen, istFirma, geladen };
}
